// src/stores/workflowStore.ts
// Zustand store for workflow state — survives tab switches

import { create } from 'zustand';
import { Message } from '../types/chat';
import { ThinkingStep } from '../components/ThinkingSteps';
import { ExecutionStage } from '../components/ExecutionStages';

export interface ChartData {
  data: string;      // base64 image
  title: string;
  chart_type: string;
}

export interface WorkflowDetails {
  toolsUsed: string[];
  sourceUrl: string;
  chartCode: string;
  elapsedSeconds: number;
}

interface WorkflowState {
  // Session
  currentSessionId: string | null;
  messages: Message[];
  input: string;

  // UI state
  workspaceModalOpen: boolean;
  workspaceContent: any;
  thinkingSteps: ThinkingStep[];
  executionStages: ExecutionStage[];
  isStreamingActive: boolean;
  loading: boolean;
  statusMessage: string;
  statusProgress: number;
  charts: ChartData[];
  workflowDetails: WorkflowDetails | null;

  // Actions
  setCurrentSessionId: (id: string | null) => void;
  setMessages: (messages: Message[] | ((prev: Message[]) => Message[])) => void;
  setInput: (input: string) => void;
  setWorkspaceModalOpen: (open: boolean) => void;
  setWorkspaceContent: (content: any) => void;
  setThinkingSteps: (steps: ThinkingStep[] | ((prev: ThinkingStep[]) => ThinkingStep[])) => void;
  setExecutionStages: (stages: ExecutionStage[] | ((prev: ExecutionStage[]) => ExecutionStage[])) => void;
  setIsStreamingActive: (active: boolean) => void;
  setLoading: (loading: boolean) => void;
  setStatusMessage: (message: string) => void;
  setStatusProgress: (progress: number) => void;
  setCharts: (charts: ChartData[] | ((prev: ChartData[]) => ChartData[])) => void;
  addChart: (chart: ChartData) => void;
  setWorkflowDetails: (details: WorkflowDetails | null) => void;

  // Bulk operations
  clearWorkflow: () => void;
  startNewWorkflow: (sessionId: string | null) => void;
}

export const useWorkflowStore = create<WorkflowState>((set) => ({
  currentSessionId: null,
  messages: [],
  input: '',
  workspaceModalOpen: false,
  workspaceContent: null,
  thinkingSteps: [],
  executionStages: [],
  isStreamingActive: false,
  loading: false,
  statusMessage: '',
  statusProgress: 0,
  charts: [],
  workflowDetails: null,

  setCurrentSessionId: (id) => set({ currentSessionId: id }),
  setMessages: (messages) =>
    set((state) => ({
      messages: typeof messages === 'function' ? messages(state.messages) : messages,
    })),
  setInput: (input) => set({ input }),
  setWorkspaceModalOpen: (open) => set({ workspaceModalOpen: open }),
  setWorkspaceContent: (content) => set({ workspaceContent: content }),
  setThinkingSteps: (steps) =>
    set((state) => ({
      thinkingSteps: typeof steps === 'function' ? steps(state.thinkingSteps) : steps,
    })),
  setExecutionStages: (stages) =>
    set((state) => ({
      executionStages: typeof stages === 'function' ? stages(state.executionStages) : stages,
    })),
  setIsStreamingActive: (active) => set({ isStreamingActive: active }),
  setLoading: (loading) => set({ loading }),
  setStatusMessage: (message) => set({ statusMessage: message }),
  setStatusProgress: (progress) => set({ statusProgress: progress }),
  setCharts: (charts) =>
    set((state) => ({
      charts: typeof charts === 'function' ? charts(state.charts) : charts,
    })),
  addChart: (chart) =>
    set((state) => {
      // Deduplicate by title to prevent double-rendering from viz_complete + workflow_complete
      if (state.charts.some(c => c.title === chart.title)) return state;
      return { charts: [...state.charts, chart] };
    }),
  setWorkflowDetails: (details) => set({ workflowDetails: details }),

  clearWorkflow: () =>
    set({
      currentSessionId: null,
      messages: [],
      input: '',
      workspaceModalOpen: false,
      workspaceContent: null,
      thinkingSteps: [],
      executionStages: [],
      isStreamingActive: false,
      loading: false,
      statusMessage: '',
      statusProgress: 0,
      charts: [],
      workflowDetails: null,
    }),

  startNewWorkflow: (sessionId) =>
    set({
      currentSessionId: sessionId,
      messages: [],
      input: '',
      workspaceModalOpen: false,
      workspaceContent: null,
      thinkingSteps: [],
      executionStages: [],
      isStreamingActive: false,
      loading: false,
      statusMessage: '',
      statusProgress: 0,
      charts: [],
      workflowDetails: null,
    }),
}));
